/**
 * Утиліти для роботи з Google Tag Manager (GTM)
 * Підтримка багатомовності і відстеження подій за мовами
 */

import { APP_CTA_VALUES, B2B_LEAD_VALUES } from '../config/appLinks';
import { getAttribution } from './attribution';

// Must match public/index.html bootstrap. Never fall back to a fake ID.
export const GTM_ID = process.env.REACT_APP_GTM_ID || 'GTM-K923GBB8';

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

/**
 * Ensure dataLayer/gtag exist. Consent defaults are owned by public/index.html —
 * never re-deny here after a stored Accept, or Ads attribution stays cookieless.
 */
export const initDataLayer = () => {
  ensureGtag();
  return window.dataLayer;
};

/**
 * Apply Consent Mode v2 update and unblock Ads identifiers after grant.
 * Default remains denied in <head>; this only runs after an explicit CMP choice
 * (or replay of a stored choice). Without ads_data_redaction=false, gclid/wbraid
 * stay redacted and Smart Bidding / GA4→Ads get almost no usable signal.
 */
export const updateConsent = (consentType) => {
  if (typeof window === 'undefined') return;

  const gtag = ensureGtag();
  const states = {
    all: {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted'
    },
    analytics: {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    },
    ads: {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'denied'
    },
    none: {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied'
    }
  };

  const next = states[consentType] || states.none;
  const adsGranted = next.ad_storage === 'granted';

  try {
    gtag('consent', 'update', next);
    // Redaction must follow ad_storage. Leaving it true after Accept is what
    // starves Google Ads conversion learning even when the CMP said "yes".
    gtag('set', 'ads_data_redaction', !adsGranted);
    gtag('set', 'url_passthrough', true);

    window.dataLayer.push({
      event: 'consent_update',
      consent_source: 'aipsyhelp_cmp',
      consent_type: consentType,
      ad_storage: next.ad_storage,
      ad_user_data: next.ad_user_data,
      ad_personalization: next.ad_personalization,
      analytics_storage: next.analytics_storage,
      ads_data_redaction: !adsGranted
    });

    if (adsGranted || next.analytics_storage === 'granted') {
      window.dataLayer.push({
        event: 'aipsyhelp_consent_granted',
        consent_type: consentType,
        ad_storage: next.ad_storage,
        analytics_storage: next.analytics_storage
      });
      // Re-arm consent-gated GTM tags once (Custom Event `pageview` only).
      const lang =
        (typeof document !== 'undefined' &&
          (document.documentElement.lang || '').split('-')[0]) ||
        '';
      pageView(window.location.pathname, document.title, lang, window.location.search);
    }
  } catch (error) {
    console.error('GTM: Помилка при оновленні згоди', error);
  }
};

/**
 * Virtual pageview for SPA navigations (all locales, news, etc.).
 * Top-level GA4 fields are required so GTM can map page_path/page_location 1:1.
 * Nested `page` kept for backward-compatible variables.
 *
 * Single measurement path: Custom Event `pageview` → GTM → GA4 G-FZH7LJLTSJ.
 * Do not call gtag(send_to: G-…) here — that double-counts with the GTM GA4 tag.
 * @param {string} path - pathname (e.g. /de/news/10)
 * @param {string} title - document title
 * @param {string} language - UI language
 * @param {string} [search] - query string including leading ?
 * @param {object} [extras] - optional page_type / content_group overrides
 */
export const pageView = (path, title, language, search = '', extras = {}) => {
  if (typeof window === 'undefined') return;
  initDataLayer();

  const page_path = path || window.location.pathname || '/';
  const page_search = typeof search === 'string' ? search : window.location.search || '';
  const page_title = title || (typeof document !== 'undefined' ? document.title : '');
  const page_location = `${window.location.origin}${page_path}${page_search}`;
  const page_type = extras.page_type || inferPageType(page_path);
  const content_group = extras.content_group || page_type;
  const lang = language || '';
  const dedupeKey = `${page_location}|${page_title}|${lang}`;
  const now = Date.now();

  // Consent replay and the route tracker can run within the same React mount.
  // Keep one canonical virtual pageview while still allowing a later consent
  // grant or a genuine SPA navigation to re-arm measurement.
  if (
    window.__aipsyhelpLastPageview?.key === dedupeKey &&
    now - window.__aipsyhelpLastPageview.at < 1500
  ) {
    return;
  }
  window.__aipsyhelpLastPageview = { key: dedupeKey, at: now };

  window.dataLayer.push({
    event: 'pageview',
    page_path,
    page_location,
    page_title,
    language: lang,
    page_type,
    content_group,
    page: {
      path: page_path,
      search: page_search,
      title: page_title,
      language: lang,
      type: page_type
    }
  });
};

/**
 * Відстеження події
 * @param {string} eventName - Назва події
 * @param {object} eventParams - Параметри події
 * @param {string} language - Мова інтерфейсу
 */
export const trackEvent = (eventName, eventParams = {}, language) => {
  if (!window.dataLayer) {
    initDataLayer();
  }
  
  window.dataLayer.push({
    event: eventName,
    language,
    ...eventParams
  });
};

/**
 * Відстеження форми
 * @param {string} formName - Назва форми
 * @param {string} formAction - Дія форми (submit, error, success)
 * @param {object} formData - Дані форми (без чутливої інформації)
 * @param {string} language - Мова інтерфейсу
 */
export const trackForm = (formName, formAction, formData = {}, language) => {
  if (!window.dataLayer) {
    initDataLayer();
  }

  const extra = typeof formData === 'string' ? { form_status: formData } : formData;

  window.dataLayer.push({
    event: 'form_interaction',
    form_name: formName,
    form_action: formAction,
    language,
    ...extra
  });
};

/**
 * GA4 + Google Ads lead conversion events for GTM.
 * Configure GTM triggers on `generate_lead` and/or `ads_conversion_lead`.
 * Do not push raw email/phone into dataLayer here — use Enhanced Conversions in GTM if needed.
 */
export const trackLeadConversion = ({
  formName,
  pageType,
  placement,
  language,
  hasCompany = false,
  value,
  currency = 'EUR',
  attribution = {},
  leadId,
  consentState
} = {}) => {
  if (!window.dataLayer) {
    initDataLayer();
  }

  if (!leadId) {
    window.dataLayer.push({
      event: 'form_submit',
      form_name: formName,
      page_type: pageType,
      form_placement: placement,
      language
    });
    return;
  }

  const resolvedAttribution =
    attribution && Object.keys(attribution).length ? attribution : getAttribution();

  const resolvedValue =
    typeof value === 'number'
      ? value
      : B2B_LEAD_VALUES[pageType] || B2B_LEAD_VALUES.contact;

  const payload = {
    lead_type: 'form',
    form_name: formName,
    page_type: pageType,
    form_placement: placement,
    lead_has_company: hasCompany ? 'yes' : 'no',
    language,
    value: resolvedValue,
    currency,
    lead_id: String(leadId),
    consent_state: consentState,
    utm_source: resolvedAttribution.utm_source || undefined,
    utm_medium: resolvedAttribution.utm_medium || undefined,
    utm_campaign: resolvedAttribution.utm_campaign || undefined,
    gclid: resolvedAttribution.gclid ? '1' : undefined
  };

  window.dataLayer.push({
    event: 'form_success',
    ...payload
  });

  window.dataLayer.push({
    event: 'generate_lead',
    ...payload
  });

  window.dataLayer.push({
    event: 'ads_conversion_lead',
    ...payload
  });
};

/**
 * App install / open intent tracking (store + web app).
 * Treat as soft lead / micro-conversion for Ads optimization.
 * GTM: trigger on `app_cta_click` and/or `ads_conversion_app`.
 */
export const trackAppConversion = ({
  platform = 'web',
  placement = 'unknown',
  language,
  destinationUrl,
  value,
  currency = 'EUR',
  attribution = {}
} = {}) => {
  if (typeof window === 'undefined') return;

  if (!window.dataLayer) {
    initDataLayer();
  }

  const resolvedAttribution =
    attribution && Object.keys(attribution).length ? attribution : getAttribution();

  const resolvedValue =
    typeof value === 'number' ? value : APP_CTA_VALUES[platform] || APP_CTA_VALUES.web;

  const pagePath = `${window.location.pathname}${window.location.search}`;

  const payload = {
    lead_type: 'app_cta',
    app_platform: platform,
    app_placement: placement,
    link_url: destinationUrl || undefined,
    page_path: pagePath,
    page_type: inferPageType(pagePath),
    language,
    value: resolvedValue,
    currency,
    utm_source: resolvedAttribution.utm_source || undefined,
    utm_medium: resolvedAttribution.utm_medium || undefined,
    utm_campaign: resolvedAttribution.utm_campaign || undefined,
    gclid: resolvedAttribution.gclid ? '1' : undefined
  };

  window.dataLayer.push({
    event: 'app_cta_click',
    ...payload
  });

  // Soft lead signal for funnel reporting
  window.dataLayer.push({
    event: 'generate_lead',
    ...payload
  });

  // Dedicated Ads conversion for install/open intent
  window.dataLayer.push({
    event: 'ads_conversion_app',
    ...payload
  });
};

function inferPageType(pathname = '') {
  if (/\/news\/\d+/.test(pathname)) return 'news_article';
  if (pathname.includes('/news')) return 'news';
  if (pathname.includes('/corporate')) return 'corporate';
  if (pathname.includes('/ngo')) return 'ngo';
  if (pathname.includes('/webinars')) return 'webinars';
  if (pathname.includes('/videos')) return 'videos';
  if (pathname.includes('/for-specialists') || pathname.includes('/careers')) return 'careers';
  if (pathname.includes('/team')) return 'team';
  if (pathname.includes('/social-mission')) return 'social_mission';
  if (pathname.includes('/ai-wellbeing')) return 'ai_wellbeing';
  if (pathname.includes('/clinical-safety')) return 'clinical_safety';
  return 'home';
}

/**
 * Відстеження помилок
 * @param {string} errorMessage - Повідомлення про помилку
 * @param {string} errorCode - Код помилки (опціонально)
 * @param {string} language - Мова інтерфейсу
 */
export const trackError = (errorMessage, errorCode, language) => {
  if (!window.dataLayer) {
    initDataLayer();
  }
  
  window.dataLayer.push({
    event: 'error',
    error_message: errorMessage,
    error_code: errorCode,
    language
  });
};

/**
 * Відстеження мовної преференції користувача
 * @param {string} language - Поточна мова
 * @param {string} previousLanguage - Попередня мова
 */
export const trackLanguageChange = (language, previousLanguage) => {
  if (!window.dataLayer) {
    initDataLayer();
  }
  
  window.dataLayer.push({
    event: 'language_change',
    current_language: language,
    previous_language: previousLanguage
  });
};

// Об'єкт з усіма утилітами для GTM
const gtmUtils = {
  initDataLayer,
  pageView,
  trackEvent,
  trackForm,
  trackLeadConversion,
  trackAppConversion,
  trackError,
  updateConsent,
  trackLanguageChange
};

export default gtmUtils; 